/**
 * DailyBriefTable
 *
 * Main data table component for the Daily Brief feature. Displays athlete data with
 * workout completion tracking, questionnaire alerts, and days remaining information.
 *
 * Features:
 * - Server-side sorting on all columns
 * - Infinite scroll for pagination
 * - Skeleton loading states
 * - Empty state with onboarding
 * - Auto-saves sort preferences to user settings
 *
 * @component
 */
import React, {
  useMemo, useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useTable, useSortBy } from 'react-table';
import IcomoonReact from 'icomoon-react';
import CompletionWeeks from './CompletionWeeks';
import AlertsColumn from './AlertsColumn';
import TableSkeleton from './TableSkeleton';
import QuestionnaireAlertsInfoModal from './QuestionnaireAlertsInfoModal';
import EmptyStateOnboarding from './EmptyStateOnboarding';
import EmptyStateFiltered from './EmptyStateFiltered';
import {
  setSortBy,
  setSortDirection,
  updateSettings,
  loadMoreDailyBrief,
} from '../ducks/dailyBriefActions';
import iconSet from '../../../shared/images/teambuildr-selection.json';

const TableWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 16px;
  width: 100%;
  background: ${(props) => (props.noBackground ? 'transparent' : '#fcfcfc')};
`;

const TableContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  width: 100%;
  border-radius: 4px;
  overflow-x: auto;
`;

const StyledTable = styled('table')`
  width: 100%;
  min-width: 1278px;
  border-collapse: collapse;
  border-spacing: 0;
  font-family: 'Nunito Sans', sans-serif;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 4px;
  table-layout: auto;
`;

const TableHead = styled('thead')`
  tr {
    height: 99px;
    background: #ffffff;
    border-bottom: 1px solid #eeeeee;
  }
`;

const TableBody = styled('tbody')`
  background: #ffffff;
`;

const TableRow = styled('tr')`
  box-sizing: border-box;
  width: 100%;
  height: 56px;
  background: #ffffff;
  border-bottom: 1px solid #eeeeee;

  &:hover {
    background-color: #fafafa;
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const TableHeaderCell = styled('th')`
  box-sizing: border-box;
  padding: 8px 16px;
  text-align: left;
  font-family: 'Nunito Sans', sans-serif;
  font-style: normal;
  vertical-align: top;
  position: relative;
  white-space: nowrap;
  cursor: ${(props) => (props.sortable ? 'pointer' : 'default')};
  user-select: none;
  background: #ffffff;
  border-left: 1px solid #eeeeee;
  height: 99px;

  &:first-of-type {
    min-width: 200px;
    width: 200px;
    border-left: none;
    border-radius: 4px 0 0 0;
  }

  &:nth-of-type(2) {
    min-width: 386px;
    width: 35%;
  }

  &:nth-of-type(3) {
    width: auto;
  }

  &:nth-of-type(4) {
    min-width: 240px;
    width: auto;
    border-radius: 0 4px 0 0;
  }
`;

const TableDataCell = styled('td')`
  box-sizing: border-box;
  padding: 8px 10px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  line-height: 150%;
  color: #444444;
  vertical-align: middle;
  background: #ffffff;
  height: 56px;
  border-left: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;

  &:first-of-type {
    min-width: 200px;
    width: 200px;
    border-left: none;
  }

  &:nth-of-type(2) {
    min-width: 386px;
    width: 35%;
    padding: 16px;
    gap: 4px;
  }

  &:nth-of-type(3) {
    width: auto;
    padding: 8px;
  }

  &:nth-of-type(4) {
    min-width: 240px;
    width: auto;
    padding: 16px;
    text-align: center;
  }

  &:nth-of-type(4):not([data-alerts-empty="true"]) {
    padding-left: 0px;
  }

  &[data-alerts-empty="true"] {
    border-bottom: 1px solid transparent;
  }
`;

const HeaderContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 0;
`;

const HeaderTitle = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  gap: 16px;
  width: 100%;
  height: 48px;
`;

const HeaderSubHeaders = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 12px;
  width: 100%;
  height: 24px;
`;

const SubHeaderItem = styled('div')`
  flex: 1;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  text-align: center;
  color: #9e9e9e;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SortButton = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px;
  gap: 8px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  background: ${(props) => (props.isActive ? 'rgba(119, 167, 255, 0.1)' : 'transparent')};

  &:hover {
    background: ${(props) => (props.isActive ? 'rgba(119, 167, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)')};
  }
`;

const SortIcon = styled('div')`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    path {
      fill: ${(props) => (props.isActive ? '#77A7FF' : '#DFDFDF')};
    }
  }
`;

const HeaderText = styled('span')`
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 700;
  font-size: 19px;
  line-height: 150%;
  color: #444444;
  flex-grow: 1;
  text-align: ${(props) => (props.centered ? 'center' : 'left')};
`;

const AlertsHeaderTitle = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  gap: 16px;
  width: 100%;
  height: 48px;
`;

const TextIconGroup = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: center;
  min-width: 0;
`;

const InfoIconCircle = styled('button')`
  background: none;
  border: solid 1px #9E9E9E;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  width: 18px;
  height: 18px;
  min-width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    border-color: #444444;
  }
`;

const AthleteCell = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 16px;
`;

const Avatar = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 100px;
  background: ${(props) => {
    if (props.image) {
      return `url(${props.image})`;
    }
    return (
      props.color || 'linear-gradient(162.65deg, #FF6600 4.46%, #F7B733 88.1%)'
    );
  }};
  background-size: cover;
  background-position: center;
  box-shadow: 0px 2px 4px rgba(48, 49, 51, 0.1),
    0px 0px 1px rgba(48, 49, 51, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  color: #ffffff;
  flex-shrink: 0;
`;

const AthleteName = styled('span')`
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  color: #444444;
  flex-grow: 1;
`;

const DaysLeftValue = styled('div')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0px 8px;
  height: 24px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  text-align: center;
  color: #444444;
  white-space: nowrap;
`;

const EmptyState = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px;
  text-align: center;

  h3 {
    font-size: 20px;
    font-weight: 600;
    color: #444444;
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    color: #666666;
    margin: 4px 0;
  }
`;

const LoadingRow = styled('tr')`
  box-sizing: border-box;
  width: 100%;
  height: 56px;
  background: #ffffff;
  border-bottom: 1px solid #e5e5e5;
`;

const LoadingCell = styled('td')`
  box-sizing: border-box;
  padding: 8px 16px;
  font-family: 'Nunito Sans';
  font-size: 16px;
  line-height: 150%;
  color: #9e9e9e;
  vertical-align: middle;
  background: #ffffff;
  height: 56px;
  border-right: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  text-align: center;

  &:last-of-type {
    border-right: none;
  }
`;

const SentinelRow = styled('tr')`
  height: 1px;

  td {
    height: 1px;
    border: none;
    padding: 0;
  }
`;

// Helper function to generate avatar colors based on name
const getAvatarColor = (firstName, lastName) => {
  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FECA57',
    '#F368E0',
    '#48DBF8',
    '#A29BFE',
    '#FD79A8',
    '#FDCB6E',
    '#6C5CE7',
    '#00B894',
    '#00CEC9',
    '#FAB1A0',
    '#74B9FF',
  ];
  const name = `${firstName}${lastName}`;
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getSortIcon = (columnId, sortBy, sortDirection) => {
  if (sortBy !== columnId) {
    return 'Arrows-Sort';
  }

  const invertedColumns = ['name', 'remainingDays'];
  let displayDirection = sortDirection;
  if (invertedColumns.includes(columnId)) {
    displayDirection = sortDirection === 'asc' ? 'desc' : 'asc';
  }

  if (displayDirection === 'desc') {
    return 'alt-down-arrow';
  }

  if (displayDirection === 'asc') {
    return 'alt-up-arrow';
  }

  return 'Arrows-Sort';
};

const DailyBriefTable = ({
  data,
  isLoading,
  isLoadingMore,
  hasMoreData,
  error,
  filterType,
  sortBy,
  sortDirection,
  hasLoadedOnce,
  accountCode,
  hasQuestionnaireAlerts,
}) => {
  // hasQuestionnaireAlerts indicates if ANY athletes have questionnaire alerts to display
  const showQuestionnaireAlerts = hasQuestionnaireAlerts === true;

  const dispatch = useDispatch();
  const { filterIds } = useSelector((state) => state.dailyBrief.ui);
  const filterSelections = useSelector(
    (state) => state.dailyBrief.data.filterSelections,
  );
  const tableContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMoreData && accountCode) {
      dispatch(
        loadMoreDailyBrief(accountCode, {
          filterType,
          filterIds: filterIds !== null ? [filterIds] : [],
          sortBy,
          sortDir: sortDirection,
        }),
      );
    }
  }, [
    isLoadingMore,
    hasMoreData,
    accountCode,
    filterType,
    filterIds,
    sortBy,
    sortDirection,
    dispatch,
  ]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMoreData || isLoadingMore) return undefined;

    const sentinelElement = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0,
      },
    );

    observer.observe(sentinelElement);

    return () => {
      observer.disconnect();
    };
  }, [loadMore, hasMoreData, isLoadingMore, data.length]);

  const weekHeaders = useMemo(() => {
    if (!data || data.length === 0) return [];

    const allWeeks = new Map();
    data.forEach((athlete) => {
      if (athlete.completionInfo) {
        athlete.completionInfo.forEach((week) => {
          const key = `${week.startDate}_${week.endDate}`;
          if (!allWeeks.has(key)) {
            allWeeks.set(key, week);
          }
        });
      }
    });

    const sortedWeeks = Array.from(allWeeks.values())
      .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
      .slice(0, 4)
      .reverse();

    return sortedWeeks.map((week) => {
      const parseLocalDate = (dateString) => {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
      };

      const backendStart = parseLocalDate(week.startDate);
      const backendEnd = parseLocalDate(week.endDate);

      const daysDiff = Math.floor(
        (backendEnd - backendStart) / (1000 * 60 * 60 * 24),
      );
      const isIncompleteWeek = daysDiff < 6;

      let displayStart;
      let displayEnd;

      if (isIncompleteWeek) {
        displayStart = new Date(backendStart);
        displayEnd = new Date(backendStart);
        displayEnd.setDate(backendStart.getDate() + 6);
      } else {
        displayStart = backendStart;
        displayEnd = backendEnd;
      }

      const formatDate = (date) => {
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const day = date.getDate();
        return `${month} ${day}`;
      };

      const startMonth = displayStart.toLocaleDateString('en-US', {
        month: 'short',
      });
      const endMonth = displayEnd.toLocaleDateString('en-US', {
        month: 'short',
      });
      let label;
      if (startMonth === endMonth) {
        label = `${startMonth} ${displayStart.getDate()}-${displayEnd.getDate()}`;
      } else {
        label = `${formatDate(displayStart)}-${formatDate(displayEnd)}`;
      }

      return {
        label,
        start: week.startDate,
        end: week.endDate,
      };
    });
  }, [data]);

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <HeaderContent>
            <HeaderTitle>
              <HeaderText>Name</HeaderText>
              <SortButton isActive={sortBy === 'name'}>
                <SortIcon isActive={sortBy === 'name'}>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={20}
                    icon={getSortIcon('name', sortBy, sortDirection)}
                  />
                </SortIcon>
              </SortButton>
            </HeaderTitle>
          </HeaderContent>
        ),
        accessor: (row) => ({
          firstName: row.firstName,
          lastName: row.lastName,
          fullName: `${row.firstName} ${row.lastName}`,
          userPic: row.userPic,
        }),
        /* eslint-disable react/prop-types */
        Cell: ({ value }) => (
          <AthleteCell>
            <Avatar
              image={value.userPic}
              color={getAvatarColor(value.firstName, value.lastName)}
            >
              {!value.userPic && (
                <>
                  {value.firstName?.[0]?.toUpperCase()}
                  {value.lastName?.[0]?.toUpperCase()}
                </>
              )}
            </Avatar>
            <AthleteName>{value.fullName}</AthleteName>
          </AthleteCell>
        ),
        /* eslint-enable react/prop-types */
        id: 'name',
        sortType: (rowA, rowB) => {
          const nameA = `${rowA.original.firstName} ${rowA.original.lastName}`;
          const nameB = `${rowB.original.firstName} ${rowB.original.lastName}`;
          return nameA.localeCompare(nameB);
        },
      },
      {
        Header: () => (
          <HeaderContent>
            <HeaderTitle>
              <HeaderText centered>Completed Workouts</HeaderText>
              <SortButton isActive={sortBy === 'completionPct'}>
                <SortIcon isActive={sortBy === 'completionPct'}>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={20}
                    icon={getSortIcon('completionPct', sortBy, sortDirection)}
                  />
                </SortIcon>
              </SortButton>
            </HeaderTitle>
            <HeaderSubHeaders>
              {weekHeaders.map((week) => (
                <SubHeaderItem key={week.label}>{week.label}</SubHeaderItem>
              ))}
            </HeaderSubHeaders>
          </HeaderContent>
        ),
        accessor: 'completionInfo',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => <CompletionWeeks completionInfo={value} />,
        id: 'completionPct',
      },
      {
        Header: () => (
          <HeaderContent>
            <HeaderTitle>
              <HeaderText centered>Days Left</HeaderText>
              <SortButton isActive={sortBy === 'remainingDays'}>
                <SortIcon isActive={sortBy === 'remainingDays'}>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={20}
                    icon={getSortIcon('remainingDays', sortBy, sortDirection)}
                  />
                </SortIcon>
              </SortButton>
            </HeaderTitle>
            <HeaderSubHeaders>
              <SubHeaderItem>To last workout</SubHeaderItem>
            </HeaderSubHeaders>
          </HeaderContent>
        ),
        accessor: 'remainingAssignedDays',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => {
          let displayValue = '—';
          if (value !== null && value !== undefined) {
            displayValue = value >= 30 ? '30+' : value;
          }
          return (
            <div style={{ textAlign: 'center' }}>
              <DaysLeftValue>{displayValue}</DaysLeftValue>
            </div>
          );
        },
        id: 'remainingDays',
      },
      {
        Header: () => (
          <HeaderContent>
            <AlertsHeaderTitle>
              <TextIconGroup>
                <HeaderText
                  style={{ margin: 0, whiteSpace: 'nowrap', flexGrow: 0 }}
                >
                  Questionnaire Alerts
                </HeaderText>
                <InfoIconCircle
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsInfoModalOpen(true);
                  }}
                  aria-label='Questionnaire Alerts information'
                >
                  <IcomoonReact
                    iconSet={iconSet}
                    icon='info'
                    size={10}
                    color='#9E9E9E'
                  />
                </InfoIconCircle>
              </TextIconGroup>
              <SortButton
                isActive={sortBy === 'alertCount'}
                style={{ flexShrink: 0 }}
              >
                <SortIcon isActive={sortBy === 'alertCount'}>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={20}
                    icon={getSortIcon('alertCount', sortBy, sortDirection)}
                  />
                </SortIcon>
              </SortButton>
            </AlertsHeaderTitle>
            <HeaderSubHeaders>
              <SubHeaderItem>Yesterday</SubHeaderItem>
              <SubHeaderItem>Today</SubHeaderItem>
            </HeaderSubHeaders>
          </HeaderContent>
        ),
        accessor: 'alertInfo',
        /* eslint-disable react/prop-types */
        Cell: ({ value, row }) => (
          <AlertsColumn
            alertInfo={value}
            showQuestionnaireAlerts={showQuestionnaireAlerts}
            isFirstRow={row.index === 0}
          />
        ),
        /* eslint-enable react/prop-types */
        id: 'alertCount',
        sortType: (rowA, rowB) => {
          const countA = rowA.original.alertInfo?.length || 0;
          const countB = rowB.original.alertInfo?.length || 0;
          return countA - countB;
        },
      },
    ],
    [weekHeaders, sortBy, sortDirection, showQuestionnaireAlerts],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      manualSortBy: true,
      disableSortRemove: true,
      initialState: {
        sortBy: [
          {
            id: sortBy,
            desc: sortDirection === 'desc',
          },
        ],
      },
    },
    useSortBy,
  );

  const handleSort = (column) => {
    if (!column.disableSortBy) {
      const newSortBy = column.id;
      let newSortDirection;

      if (sortBy === column.id) {
        newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        const invertedColumns = ['name', 'remainingDays'];
        newSortDirection = invertedColumns.includes(column.id) ? 'asc' : 'desc';
      }

      dispatch(setSortBy(newSortBy));
      dispatch(setSortDirection(newSortDirection));

      const newSettings = {
        defaultSortBy: newSortBy,
        defaultSortOrder: newSortDirection,
      };

      if (filterType != null) {
        newSettings.defaultFilterType = filterType;
      }
      if (filterIds != null) {
        newSettings.defaultFilterIds = filterIds;
      }

      dispatch(updateSettings(accountCode, newSettings));
    }
  };

  if (error) {
    return (
      <TableWrapper>
        <EmptyState>
          <h3>Error Loading Daily Brief</h3>
          <p>{error.message || 'An error occurred while loading the data.'}</p>
        </EmptyState>
      </TableWrapper>
    );
  }

  const shouldShowEmptyState = hasLoadedOnce && !isLoading && (!data || data.length === 0);
  const isInitialLoad = !hasLoadedOnce;
  if (!shouldShowEmptyState && (isLoading || isInitialLoad) && (!data || data.length === 0)) {
    return (
      <TableWrapper>
        <TableContainer>
          <StyledTable>
            <TableHead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <TableHeaderCell
                      {...column.getHeaderProps()}
                      sortable={!column.disableSortBy}
                    >
                      {column.render('Header')}
                    </TableHeaderCell>
                  ))}
                </tr>
              ))}
            </TableHead>
            <TableBody>
              <TableSkeleton rows={10} />
            </TableBody>
          </StyledTable>
        </TableContainer>
      </TableWrapper>
    );
  }

  if (shouldShowEmptyState) {
    const handleAddAthletes = () => {
      window.location.href = 'https://support.teambuildr.com/article/jmvqe3qglh-getting-started';
    };

    const hasFilterApplied = filterType !== null && filterIds !== null;

    if (hasFilterApplied) {
      const selectedGroup = filterSelections?.find((group) => group.id === filterIds);
      const groupName = selectedGroup?.name || 'this group';

      return (
        <TableWrapper>
          <EmptyStateFiltered filterMessage={`in ${groupName}`} />
        </TableWrapper>
      );
    }

    return (
      <EmptyStateOnboarding
        onAddAthletes={handleAddAthletes}
      />
    );
  }

  return (
    <TableWrapper>
      <TableContainer ref={tableContainerRef}>
        <StyledTable {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <TableHeaderCell
                    {...column.getHeaderProps()}
                    onClick={() => handleSort(column)}
                    sortable={!column.disableSortBy}
                  >
                    {column.render('Header')}
                  </TableHeaderCell>
                ))}
              </tr>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {isLoading ? (
              <TableSkeleton rows={data?.length || 10} />
            ) : (
              <>
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <TableRow {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        const isAlertsColumn = cell.column.id === 'alertCount';
                        const alertsEmpty = isAlertsColumn && !showQuestionnaireAlerts;

                        return (
                          <TableDataCell
                            {...cell.getCellProps()}
                            data-alerts-empty={alertsEmpty ? 'true' : undefined}
                          >
                            {cell.render('Cell')}
                          </TableDataCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {isLoadingMore && hasMoreData && (
                  <LoadingRow>
                    <LoadingCell colSpan={4}>Loading more...</LoadingCell>
                  </LoadingRow>
                )}
                {hasMoreData && !isLoading && (
                  <SentinelRow ref={sentinelRef}>
                    <td colSpan={4} />
                  </SentinelRow>
                )}
              </>
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <QuestionnaireAlertsInfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </TableWrapper>
  );
};

DailyBriefTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      userPic: PropTypes.string,
      completionInfo: PropTypes.arrayOf(
        PropTypes.shape({
          assignedCount: PropTypes.number,
          completedCount: PropTypes.number,
          completedPct: PropTypes.number,
          startDate: PropTypes.string,
          endDate: PropTypes.string,
        }),
      ),
      remainingAssignedDays: PropTypes.number,
      alertInfo: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          date: PropTypes.string,
          title: PropTypes.string,
          abbr: PropTypes.string,
          description: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
          ]),
        }),
      ),
    }),
  ),
  settings: PropTypes.shape({
    defaultSortBy: PropTypes.string,
    defaultSortOrder: PropTypes.string,
    defaultFilterType: PropTypes.number,
    defaultFilterIds: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.arrayOf(PropTypes.number),
    ]),
  }),
  isLoading: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  hasMoreData: PropTypes.bool,
  hasQuestionnaireAlerts: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
  error: PropTypes.oneOfType([
    PropTypes.instanceOf(Error),
    PropTypes.string,
    PropTypes.shape({ message: PropTypes.string }),
    PropTypes.oneOf([null]),
  ]),
  filterType: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  hasLoadedOnce: PropTypes.bool,
  accountCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DailyBriefTable;

DailyBriefTable.defaultProps = {
  data: [],
  settings: {},
  isLoading: false,
  isLoadingMore: false,
  hasMoreData: true,
  hasQuestionnaireAlerts: null,
  error: null,
  filterType: null,
  sortBy: 'name',
  sortDirection: 'asc',
  hasLoadedOnce: false,
  accountCode: null,
};
